import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const services = [
  {
    id: 'strategy',
    icon: 'ri-lightbulb-flash-line',
    title: 'AI 战略咨询',
    subtitle: 'AI Strategy Consulting',
    problem: '企业想推进 AI 转型，但不知道从哪里开始，也不知道哪些方向真正有价值。',
    whyNot: '传统咨询公司给的是通用框架和 PPT，缺乏对软件工程实际情况的理解，落地性差。',
    howWeDo: '深入了解企业现有技术栈、团队能力和业务目标，识别最高价值的 AI 切入点，制定可执行的转型路径。',
    deliverables: ['AI 转型诊断报告', '优先级排序的切入点清单', '定制化落地路径', '风险评估与应对策略'],
    coop: '通常以 2-4 周的诊断项目形式进行，可延伸为长期陪跑。',
    color: '#2563EB',
  },
  {
    id: 'training',
    icon: 'ri-graduation-cap-line',
    title: 'AI 研发培训',
    subtitle: 'AI Development Training',
    problem: '团队想学习 AI 协作，但市面上的培训都是工具使用技巧，无法真正提升团队生产力。',
    whyNot: '课堂培训脱离真实项目，学完之后回到工作中无法应用。工具技巧不等于工程能力。',
    howWeDo: '在你们自己的真实项目中进行实战带教。我们不讲理论，直接在项目里手把手建立 AI 协作工作流。',
    deliverables: ['AI 协作工作流建立', '团队 Prompt 库', '工程规范文档', '持续优化机制'],
    coop: '通常以 4-8 周的驻场或远程带教形式进行。',
    color: '#F97316',
  },
  {
    id: 'dev',
    icon: 'ri-code-box-line',
    title: '工具软件定制开发',
    subtitle: 'Custom Software Development',
    problem: '企业有明确的软件需求，但传统外包开发周期长、成本高、质量不稳定。',
    whyNot: '传统外包依赖人力堆积，沟通成本高，交付后维护困难，无法快速响应需求变化。',
    howWeDo: '用 AI 协作方式进行软件开发，更短的交付周期、更低的成本、更高的代码质量。全程透明，客户深度参与。',
    deliverables: ['可交付的软件产品', '完整的技术文档', '源代码所有权', '部署和运维支持'],
    coop: '以项目制为主，明确交付目标和时间节点。',
    color: '#0F172A',
  },
  {
    id: 'legacy',
    icon: 'ri-refresh-line',
    title: '老系统升级改造',
    subtitle: 'Legacy System Modernization',
    problem: '老系统积累了大量技术债，想改但改不动，想引入 AI 但 AI 根本无法理解项目。',
    whyNot: '老系统改造风险高，传统方式需要大量人力，而且很容易引入新的问题。',
    howWeDo: '先重构工程环境，让 AI 能够理解项目。再用 AI 协作方式逐步改造，降低风险，提高效率。',
    deliverables: ['工程环境重构方案', '分阶段改造计划', '改造后的系统', 'AI 可理解的代码结构'],
    coop: '通常以阶段性陪跑形式进行，分阶段交付，降低风险。',
    color: '#2563EB',
  },
  {
    id: 'agent',
    icon: 'ri-robot-line',
    title: 'Agent 平台定制',
    subtitle: 'Agent Platform Customization',
    problem: '企业想构建 AI Agent 能力，但不知道如何将业务流程与 AI 能力深度融合。',
    whyNot: '通用 Agent 平台无法满足企业特定业务需求，需要深度定制才能真正发挥价值。',
    howWeDo: '深入理解业务流程，设计专属 Agent 架构，构建与现有系统深度集成的 AI Agent 平台。',
    deliverables: ['Agent 架构设计', '定制化 Agent 平台', '业务流程集成', '持续优化支持'],
    coop: '以项目制为主，可延伸为长期技术支持。',
    color: '#F97316',
  },
];

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function ServicesPage() {
  const [activeService, setActiveService] = useState(services[0].id);
  const active = services.find((s) => s.id === activeService) ?? services[0];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/bg-tech-dark.svg"
            alt="services hero"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/60 to-[#0F172A]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-widest text-white/40 mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
              Services
            </span>
            <h1
              className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
            >
              服务
            </h1>
            <p
              className="text-lg text-white/50 leading-relaxed"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              每项服务都围绕一个核心目标：帮助企业完成 AI 时代的软件生产方式重构。
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are — 新增区块 */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left: Statement */}
              <div>
                <span className="text-xs uppercase tracking-widest text-[#64748B] mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Who We Are
                </span>
                <h2
                  className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6 leading-tight"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
                >
                  我们是<span className="text-[#F97316]">深度参与</span>的<br />
                  技术伙伴
                </h2>
                <div className="space-y-4">
                  <p className="text-base text-[#64748B] leading-relaxed" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                    宽思智能不是软件外包，不是工具经销商，也不是讲完课就离场的培训机构。我们和客户的团队坐在一起，把真实的业务问题看清楚，然后一步一步走到结果。
                  </p>
                  <p className="text-base text-[#64748B] leading-relaxed" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                    我们关心的不是哪个模型更强、哪个工具更新，而是：<strong className="text-[#0F172A]">你的团队有没有能力在真实项目的压力下持续交付</strong>。
                  </p>
                  <p className="text-base text-[#64748B] leading-relaxed" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                    工具会过时，能力不会。我们做的每一件事，最终都指向同一个目标：让 AI 能力<strong className="text-[#0F172A]">内化成客户团队自己的能力</strong>，而不是对外部服务的长期依赖。
                  </p>
                </div>

                <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
                  {[
                    { num: '5 人 / 批', label: '小班实操' },
                    { num: '10+', label: '定制 Agent / Skill' },
                    { num: '5 - 12 天', label: '典型驻场周期' },
                  ].map((stat) => (
                    <div key={stat.label}>
                      <p className="text-base md:text-lg font-bold text-[#0F172A]" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{stat.num}</p>
                      <p className="text-xs text-[#64748B] mt-0.5" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Image */}
              <div className="relative">
                <div className="rounded-2xl overflow-hidden border border-slate-100">
                  <img
                    src="/bg-team.svg"
                    alt="宽思智能团队交付软件"
                    className="w-full h-auto object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-[#0F172A] text-white rounded-xl px-5 py-3 shadow-lg">
                  <p className="text-xs text-white/60 mb-0.5">交付模式</p>
                  <p className="text-sm font-semibold">驻场 + 远程协作</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Service Selector */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-3 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                What We Deliver
              </span>
              <h2
                className="text-2xl md:text-3xl font-bold text-[#0F172A]"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
              >
                五项核心服务
              </h2>
            </div>
            <div className="flex flex-wrap gap-3 mb-12">
              {services.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveService(s.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeService === s.id
                      ? 'bg-[#0F172A] text-white'
                      : 'bg-white text-[#64748B] border border-slate-100 hover:border-slate-200'
                  }`}
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  <i className={`${s.icon} text-sm`} />
                  {s.title}
                </button>
              ))}
            </div>
          </FadeIn>

          {/* Active Service Detail */}
          <FadeIn delay={100}>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* Left Info */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl p-8 border border-slate-100 h-full">
                  <div
                    className="w-14 h-14 flex items-center justify-center rounded-2xl mb-6"
                    style={{ backgroundColor: `${active.color}15` }}
                  >
                    <i className={`${active.icon} text-2xl`} style={{ color: active.color }} />
                  </div>
                  <h2
                    className="text-2xl font-bold text-[#0F172A] mb-1"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {active.title}
                  </h2>
                  <p
                    className="text-xs text-[#64748B] mb-8"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {active.subtitle}
                  </p>

                  <div className="mb-6">
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>交付物</p>
                    <ul className="flex flex-col gap-2.5">
                      {active.deliverables.map((d) => (
                        <li key={d} className="flex items-center gap-2.5">
                          <i className="ri-check-line text-sm" style={{ color: active.color }} />
                          <span className="text-sm text-[#0F172A]" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-6 border-t border-slate-100">
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>合作方式</p>
                    <p className="text-sm text-[#64748B] leading-relaxed" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                      {active.coop}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Detail */}
              <div className="lg:col-span-3 flex flex-col gap-5">
                {[
                  { label: '问题是什么', icon: 'ri-question-line', content: active.problem, accent: '#F97316' },
                  { label: '为什么传统方法不行', icon: 'ri-close-circle-line', content: active.whyNot, accent: '#64748B' },
                  { label: '宽思怎么做', icon: 'ri-arrow-right-circle-line', content: active.howWeDo, accent: '#2563EB' },
                ].map((block) => (
                  <div key={block.label} className="bg-white rounded-2xl p-7 border border-slate-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${block.accent}15` }}
                      >
                        <i className={`${block.icon} text-sm`} style={{ color: block.accent }} />
                      </div>
                      <span
                        className="text-sm font-semibold text-[#0F172A]"
                        style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                      >
                        {block.label}
                      </span>
                    </div>
                    <p
                      className="text-sm text-[#64748B] leading-relaxed"
                      style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                    >
                      {block.content}
                    </p>
                  </div>
                ))}

                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  预约交流，了解这项服务
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Delivery Process — 新增区块 */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                How We Work
              </span>
              <h2
                className="text-3xl md:text-4xl font-bold text-[#0F172A]"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
              >
                我们的交付方式
              </h2>
              <p className="text-base text-[#64748B] mt-4 max-w-xl mx-auto" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                不是外包，不是培训，是深度参与式的软件交付。
              </p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: '01',
                title: '驻场诊断',
                desc: '我们进入你的团队，了解真实的技术栈、业务流程和团队能力。不是远程问卷，是面对面深度沟通。',
                icon: 'ri-search-line',
                color: '#2563EB',
              },
              {
                step: '02',
                title: '共建交付',
                desc: '不是把需求文档丢给你然后等验收。我们和你的团队一起写代码、一起调试、一起交付。你看到的是每天的进度，不是最后 surprises。',
                icon: 'ri-team-line',
                color: '#F97316',
              },
              {
                step: '03',
                title: '持续优化',
                desc: '我们不是交付完就消失的团队。系统上线后，我们持续跟踪运行状态，根据实际使用反馈迭代优化，确保交付物真正产生业务价值。',
                icon: 'ri-refresh-line',
                color: '#0F172A',
              },
            ].map((item, idx) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full mx-auto mb-4" style={{ backgroundColor: `${item.color}10` }}>
                  <i className={`${item.icon} text-2xl`} style={{ color: item.color }} />
                </div>
                <p className="text-sm font-bold text-[#0F172A] mb-2" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                  {item.title}
                </p>
                <p className="text-sm text-[#64748B] leading-relaxed" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Services Grid */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                All Services
              </span>
              <h2
                className="text-3xl md:text-4xl font-bold text-[#0F172A]"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
              >
                全部服务一览
              </h2>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, idx) => (
              <FadeIn key={s.id} delay={idx * 80}>
                <button
                  onClick={() => {
                    setActiveService(s.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="group w-full text-left bg-white rounded-2xl p-7 border border-slate-100 hover:border-[#2563EB]/30 transition-all duration-300 cursor-pointer"
                >
                  <div
                    className="w-11 h-11 flex items-center justify-center rounded-xl mb-5 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${s.color}10` }}
                  >
                    <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
                  </div>
                  <h3
                    className="text-base font-bold text-[#0F172A] mb-2"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className="text-sm text-[#64748B] leading-relaxed line-clamp-2"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {s.problem}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-xs font-medium text-[#64748B] group-hover:text-[#0F172A] transition-colors">
                    <span style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>了解详情</span>
                    <i className="ri-arrow-right-line text-xs" />
                  </div>
                </button>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <FadeIn>
            <h2
              className="text-2xl md:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              不确定哪项服务适合你？
            </h2>
            <p
              className="text-base text-white/50 mb-8"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              预约一次免费交流，我们帮你找到最合适的切入点。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="px-7 py-3.5 rounded-full bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
              >
                预约交流
              </Link>
              <Link
                to="/#premise"
                className="px-7 py-3.5 rounded-full border border-white/20 text-white text-sm font-medium whitespace-nowrap cursor-pointer hover:border-white/40 transition-colors"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
              >
                了解方法论
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
